import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import {
    Box,
    Grid,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Switch,
    FormControlLabel,
    Alert,
    InputAdornment,
    Tabs,
    Tab,
    Divider,
    IconButton,
    Paper,
    Tooltip,
    Chip,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    LocalHospital as ClinicIcon,
    Person as PersonIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { doctorAPI, doctorSpecializationAPI } from '../../services/api';
import { doctorSchema } from '../../validations/schemas';
import { representativeService } from '../../services/representativeService';
import { searchSpecializations } from '../../services/optimizedSearchService';
import OptimizedSearchableAutocomplete from '../../components/common/OptimizedSearchableAutocomplete';

// ─── Tab Panel Helper ────────────────────────────────────────────────────────

const TabPanel = ({ children, value, index }) => (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
        {value === index && children}
    </Box>
);

// ─── Empty Clinic Factory ────────────────────────────────────────────────────

const makeEmptyClinic = () => ({
    name: '',
    contactNumber: '',
    representative: '',
    isActive: true,
    notes: '',
    address: {
        street: '',
        city: '',
        state: '',
        country: 'Egypt',
    },
});

// ─── Single Clinic Form ──────────────────────────────────────────────────────

const ClinicForm = ({
    clinic,
    index,
    representatives,
    onUpdate,
    onRemove,
    t,
}) => {
    const [expanded, setExpanded] = useState(true);

    const handleField = (field, value) => {
        onUpdate(index, { ...clinic, [field]: value });
    };

    const handleAddress = (field, value) => {
        onUpdate(index, {
            ...clinic,
            address: { ...clinic.address, [field]: value },
        });
    };

    const handlePhone = (e) => {
        handleField('contactNumber', e.target.value);
    };

    return (
        <Paper
            variant="outlined"
            sx={{ p: 2, mb: 2, borderRadius: 2 }}
        >
            {/* Clinic Header */}
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={expanded ? 2 : 0}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <ClinicIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight="bold">
                        {clinic.name
                            ? clinic.name
                            : `${t('doctors.clinics.clinic', 'Clinic')} ${index + 1}`}
                    </Typography>
                    {!clinic.isActive && (
                        <Chip
                            label={t('doctors.inactive', 'Inactive')}
                            size="small"
                            color="default"
                        />
                    )}
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Tooltip title={expanded
                        ? t('common.collapse', 'Collapse')
                        : t('common.expand', 'Expand')
                    }>
                        <IconButton
                            size="small"
                            onClick={() => setExpanded(prev => !prev)}
                        >
                            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t('doctors.clinics.removeClinic', 'Remove clinic')}>
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => onRemove(index)}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {expanded && (
                <Grid container spacing={2}>
                    {/* Clinic Name */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            required
                            size="small"
                            label={t('doctors.clinics.clinicName', 'Clinic Name')}
                            value={clinic.name}
                            onChange={(e) => handleField('name', e.target.value)}
                            error={!clinic.name}
                            helperText={!clinic.name
                                ? t('doctors.clinics.nameRequired', 'Clinic name is required')
                                : ''}
                        />
                    </Grid>

                    {/* Clinic Phone */}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label={t('doctors.contactNumber', 'Contact Number')}
                            value={clinic.contactNumber}
                            onChange={handlePhone}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">+20</InputAdornment>
                                ),
                                inputProps: { maxLength: 10, pattern: '[0-9]{10}' },
                            }}
                            helperText={t('doctors.phoneFormatHelper', '')}
                        />
                    </Grid>

                    {/* Representative */}
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel>
                                {t('representatives.title', 'Representative')}
                            </InputLabel>
                            <Select
                                value={clinic.representative || ''}
                                onChange={(e) =>
                                    handleField('representative', e.target.value)
                                }
                                label={t('representatives.title', 'Representative')}
                            >
                                <MenuItem value="">
                                    {t('representatives.noRepresentative', 'No Representative')}
                                </MenuItem>
                                {representatives.map((rep) => (
                                    <MenuItem key={rep._id} value={rep._id}>
                                        {rep.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Active Toggle */}
                    <Grid item xs={12} sm={6} display="flex" alignItems="center">
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={clinic.isActive}
                                    onChange={(e) =>
                                        handleField('isActive', e.target.checked)
                                    }
                                    size="small"
                                />
                            }
                            label={t('doctors.active', 'Active')}
                        />
                    </Grid>

                    {/* Address Section */}
                    <Grid item xs={12}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            mb={1}
                        >
                            {t('doctors.address', 'Address')}
                        </Typography>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={t('doctors.address.street', 'Street')}
                                    value={clinic.address.street}
                                    onChange={(e) =>
                                        handleAddress('street', e.target.value)
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={t('doctors.address.city', 'City')}
                                    value={clinic.address.city}
                                    onChange={(e) =>
                                        handleAddress('city', e.target.value)
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={t('doctors.address.state', 'State')}
                                    value={clinic.address.state}
                                    onChange={(e) =>
                                        handleAddress('state', e.target.value)
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label={t('doctors.address.country', 'Country')}
                                    value={clinic.address.country}
                                    onChange={(e) =>
                                        handleAddress('country', e.target.value)
                                    }
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Notes */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            multiline
                            rows={2}
                            label={t('doctors.clinics.notes', 'Notes')}
                            value={clinic.notes}
                            onChange={(e) => handleField('notes', e.target.value)}
                            inputProps={{ maxLength: 500 }}
                        />
                    </Grid>
                </Grid>
            )}
        </Paper>
    );
};

// ─── Main Dialog ─────────────────────────────────────────────────────────────

const AddDoctorDialog = ({ open, onClose, doctor, onSuccess }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [representatives, setRepresentatives] = useState([]);
    const [specializations, setSpecializations] = useState([]);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    // Local clinics state (managed separately from formik for flexibility)
    const [clinics, setClinics] = useState([]);
    // Track which clinic names are missing (for validation feedback)
    const [clinicErrors, setClinicErrors] = useState([]);

    // ── Memoized initial options ──────────────────────────────────────────
    const specializationOptions = useMemo(
        () => specializations.slice(0, 20),
        [specializations]
    );

    const getSpecializationOptionLabel = useCallback(
        (option) => option?.name || '',
        []
    );

    // ── Formik ────────────────────────────────────────────────────────────
    const formik = useFormik({
        initialValues: {
            name: '',
            specialization: '',
            licenseNumber: '',
            contactNumber: '',
            address: {
                street: '',
                city: '',
                state: '',
                country: 'Egypt',
            },
            active: true,
            representative: '',
        },
        validationSchema: doctorSchema,
        onSubmit: async (values) => {
            setFormSubmitted(true);

            // Validate clinics: every clinic must have a name
            const errors = clinics.map((c) => !c.name.trim());
            setClinicErrors(errors);
            if (errors.some(Boolean)) {
                setActiveTab(1); // Switch to clinics tab to show errors
                toast.error(
                    t(
                        'doctors.clinics.nameRequiredAll',
                        'All clinics must have a name.'
                    )
                );
                return;
            }

            try {
                setLoading(true);

                // Format doctor contact number
                let contact = values.contactNumber;
                if (contact) {
                    contact = contact.replace(/\D/g, '');
                    if (contact.startsWith('0')) contact = contact.substring(1);
                    contact = contact.slice(-10);
                }

                // Format clinic phone numbers
                const formattedClinics = clinics.map((c) => {
                    let clinicContact = c.contactNumber;
                    if (clinicContact) {
                        clinicContact = clinicContact.replace(/\D/g, '');
                        if (clinicContact.startsWith('0'))
                            clinicContact = clinicContact.substring(1);
                        clinicContact = clinicContact.slice(-10);
                    }
                    return {
                        ...c,
                        contactNumber: clinicContact
                            ? `+20${clinicContact}`
                            : '',
                        // Clean up empty representative
                        representative: c.representative || null,
                    };
                });

                const backendDoctor = {
                    name: values.name,
                    specialization: values.specialization,
                    licenseNumber: values.licenseNumber,
                    contactNumber: contact ? `+20${contact}` : '',
                    address: values.address,
                    isActive: values.active ?? true,
                    clinics: formattedClinics,
                };

                // Handle legacy top-level representative
                if (doctor) {
                    backendDoctor.representative = values.representative || null;
                } else if (values.representative) {
                    backendDoctor.representative = values.representative;
                }

                if (doctor) {
                    await doctorAPI.update(doctor._id, backendDoctor);
                    toast.success(t('doctors.updateSuccess'));
                } else {
                    await doctorAPI.create(backendDoctor);
                    toast.success(t('doctors.createSuccess'));
                }

                onSuccess();
                handleClose();
            } catch (err) {
                let errorMessage = t('doctors.error');
                if (err.response?.data) {
                    const errorData = err.response.data;
                    if (errorData.errors && Array.isArray(errorData.errors)) {
                        const validationErrors = errorData.errors
                            .map((e) => e.message)
                            .join(', ');
                        errorMessage = `Validation errors: ${validationErrors}`;
                        const formikErrors = {};
                        errorData.errors.forEach((error) => {
                            if (error.field) formikErrors[error.field] = error.message;
                        });
                        formik.setErrors(formikErrors);
                    } else if (errorData.message) {
                        errorMessage = errorData.message;
                    }
                } else if (err.message) {
                    errorMessage = err.message;
                }
                toast.error(
                    `Failed to ${doctor ? 'update' : 'create'} doctor: ${errorMessage}`
                );
            } finally {
                setLoading(false);
            }
        },
    });

    // ── Selected specialization object ────────────────────────────────────
    const selectedSpecialization = useMemo(() => {
        if (!formik.values.specialization) return null;
        return (
            specializations.find(
                (spec) => spec._id === formik.values.specialization
            ) || null
        );
    }, [formik.values.specialization, specializations]);

    // ── Open / populate ───────────────────────────────────────────────────
    useEffect(() => {
        if (open) {
            fetchData();
            setActiveTab(0);
            setClinicErrors([]);

            if (doctor) {
                formik.setValues({
                    name: doctor.name || '',
                    specialization: doctor.specialization
                        ? typeof doctor.specialization === 'object'
                            ? doctor.specialization._id
                            : String(doctor.specialization)
                        : '',
                    licenseNumber: doctor.licenseNumber || '',
                    contactNumber: doctor.contactNumber?.startsWith('+20')
                        ? doctor.contactNumber.substring(3)
                        : doctor.contactNumber || '',
                    address: doctor.address || {
                        street: '',
                        city: '',
                        state: '',
                        country: 'Egypt',
                    },
                    active: doctor.isActive ?? true,
                    representative: doctor.representative
                        ? typeof doctor.representative === 'object'
                            ? doctor.representative._id
                            : String(doctor.representative)
                        : '',
                });

                // Populate clinics from doctor data
                setClinics(
                    (doctor.clinics || []).map((c) => ({
                        _id: c._id,
                        name: c.name || '',
                        contactNumber: c.contactNumber?.startsWith('+20')
                            ? c.contactNumber.substring(3)
                            : c.contactNumber || '',
                        representative: c.representative
                            ? typeof c.representative === 'object'
                                ? c.representative._id
                                : String(c.representative)
                            : '',
                        isActive: c.isActive ?? true,
                        notes: c.notes || '',
                        address: c.address || {
                            street: '',
                            city: '',
                            state: '',
                            country: 'Egypt',
                        },
                    }))
                );
            } else {
                formik.resetForm();
                setClinics([]);
            }
            setFormSubmitted(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, doctor]);

    const fetchData = async () => {
        try {
            const repsRes =
                await representativeService.getRepresentativesForDropdown();
            setRepresentatives(repsRes.data || []);
        } catch (err) {
            console.error('Error fetching representatives:', err);
            setRepresentatives([]);
        }
        try {
            const specRes = await doctorSpecializationAPI.getAll();
            setSpecializations(specRes.data?.data || []);
        } catch (err) {
            console.error('Error fetching specializations:', err);
            setSpecializations([]);
        }
    };

    const handleClose = () => {
        formik.resetForm();
        setClinics([]);
        setClinicErrors([]);
        setFormSubmitted(false);
        setActiveTab(0);
        onClose();
    };

    const handleSpecializationChange = useCallback((event, newValue) => {
        formik.setFieldValue('specialization', newValue ? newValue._id : '');
        formik.setFieldTouched('specialization', true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Clinic helpers ────────────────────────────────────────────────────
    const handleAddClinic = () => {
        setClinics((prev) => [...prev, makeEmptyClinic()]);
        setClinicErrors((prev) => [...prev, false]);
        // Auto-switch to clinics tab
        setActiveTab(1);
    };

    const handleUpdateClinic = useCallback((index, updatedClinic) => {
        setClinics((prev) => {
            const next = [...prev];
            next[index] = updatedClinic;
            return next;
        });
        // Clear error for this clinic if name is now filled
        if (updatedClinic.name) {
            setClinicErrors((prev) => {
                const next = [...prev];
                next[index] = false;
                return next;
            });
        }
    }, []);

    const handleRemoveClinic = useCallback((index) => {
        setClinics((prev) => prev.filter((_, i) => i !== index));
        setClinicErrors((prev) => prev.filter((_, i) => i !== index));
    }, []);

    // ── Render ────────────────────────────────────────────────────────────
    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {doctor ? t('doctors.editDoctor') : t('doctors.addDoctor')}
            </DialogTitle>

            <DialogContent>
                {/* Tabs */}
                <Tabs
                    value={activeTab}
                    onChange={(_, val) => setActiveTab(val)}
                    sx={{ borderBottom: 1, borderColor: 'divider', mb: 0 }}
                >
                    <Tab
                        icon={<PersonIcon fontSize="small" />}
                        iconPosition="start"
                        label={t('doctors.tabs.general', 'General Info')}
                    />
                    <Tab
                        icon={<ClinicIcon fontSize="small" />}
                        iconPosition="start"
                        label={
                            <Box display="flex" alignItems="center" gap={0.5}>
                                {t('doctors.tabs.clinics', 'Clinics')}
                                {clinics.length > 0 && (
                                    <Chip
                                        label={clinics.length}
                                        size="small"
                                        color="primary"
                                        sx={{ height: 18, fontSize: 11 }}
                                    />
                                )}
                            </Box>
                        }
                    />
                </Tabs>

                <Box
                    component="form"
                    id="doctor-form"
                    onSubmit={formik.handleSubmit}
                    noValidate
                    sx={{ mt: 1 }}
                >
                    {/* ── General errors alert ── */}
                    {formSubmitted &&
                        Object.keys(formik.errors).length > 0 &&
                        activeTab === 0 && (
                            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
                                <Typography variant="body2">
                                    {t(
                                        'common.fixErrors',
                                        'Please fix the following errors:'
                                    )}
                                </Typography>
                                <ul
                                    style={{
                                        margin: '8px 0',
                                        paddingLeft: '20px',
                                    }}
                                >
                                    {Object.entries(formik.errors).map(
                                        ([field, error]) => (
                                            <li key={field}>
                                                <Typography variant="body2">
                                                    <strong>{field}:</strong>{' '}
                                                    {typeof error === 'object'
                                                        ? JSON.stringify(error)
                                                        : error}
                                                </Typography>
                                            </li>
                                        )
                                    )}
                                </ul>
                            </Alert>
                        )}

                    {/* ════════════════════════════════════════════════════
                        TAB 0 — GENERAL INFO
                    ════════════════════════════════════════════════════ */}
                    <TabPanel value={activeTab} index={0}>
                        <Grid container spacing={2}>
                            {/* Name */}
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t('doctors.name')}
                                    name="name"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.name &&
                                        Boolean(formik.errors.name)
                                    }
                                    helperText={
                                        formik.touched.name &&
                                        formik.errors.name
                                    }
                                    required
                                />
                            </Grid>

                            {/* Specialization */}
                            <Grid item xs={12} sm={6}>
                                <OptimizedSearchableAutocomplete
                                    label={t('doctors.specialization')}
                                    searchFunction={searchSpecializations}
                                    value={selectedSpecialization}
                                    onChange={handleSpecializationChange}
                                    getOptionLabel={getSpecializationOptionLabel}
                                    isOptionEqualToValue={(option, value) =>
                                        option?._id === value?._id
                                    }
                                    initialOptions={specializationOptions}
                                    error={
                                        formik.touched.specialization &&
                                        Boolean(formik.errors.specialization)
                                    }
                                    helperText={
                                        formik.touched.specialization &&
                                        formik.errors.specialization
                                    }
                                    placeholder={
                                        t('doctors.searchSpecialization') ||
                                        'Search specialization...'
                                    }
                                    noOptionsText={
                                        t('doctors.noSpecializationsFound') ||
                                        'No specializations found'
                                    }
                                    minSearchLength={1}
                                    disabled={loading}
                                />
                            </Grid>

                            {/* License Number */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t('doctors.licenseNumber')}
                                    name="licenseNumber"
                                    value={formik.values.licenseNumber}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.licenseNumber &&
                                        Boolean(formik.errors.licenseNumber)
                                    }
                                    helperText={
                                        formik.touched.licenseNumber &&
                                        formik.errors.licenseNumber
                                    }
                                />
                            </Grid>

                            {/* Contact Number */}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={t('doctors.contactNumber')}
                                    name="contactNumber"
                                    value={formik.values.contactNumber}
                                    onChange={(e) =>
                                        formik.setFieldValue(
                                            'contactNumber',
                                            e.target.value
                                        )
                                    }
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.contactNumber &&
                                        Boolean(formik.errors.contactNumber)
                                    }
                                    helperText={
                                        (formik.touched.contactNumber &&
                                            formik.errors.contactNumber) ||
                                        t('doctors.phoneFormatHelper')
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                +20
                                            </InputAdornment>
                                        ),
                                        inputProps: {
                                            maxLength: 10,
                                            pattern: '[0-9]{10}',
                                        },
                                    }}
                                />
                            </Grid>

                            {/* Legacy Representative (top-level) */}
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>
                                        {t('representatives.title')}
                                    </InputLabel>
                                    <Select
                                        name="representative"
                                        value={formik.values.representative || ''}
                                        onChange={formik.handleChange}
                                        label={t('representatives.title')}
                                    >
                                        <MenuItem value="">
                                            {t(
                                                'representatives.noRepresentative',
                                                'No Representative'
                                            )}
                                        </MenuItem>
                                        {representatives.map((rep) => (
                                            <MenuItem
                                                key={rep._id}
                                                value={rep._id}
                                            >
                                                {rep.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Address Section */}
                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }}>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {t('doctors.address', 'Address')}
                                    </Typography>
                                </Divider>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t('doctors.address.street')}
                                    name="address.street"
                                    value={formik.values.address.street}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.address?.street &&
                                        Boolean(formik.errors.address?.street)
                                    }
                                    helperText={
                                        formik.touched.address?.street &&
                                        formik.errors.address?.street
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label={t('doctors.address.city')}
                                    name="address.city"
                                    value={formik.values.address.city}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.address?.city &&
                                        Boolean(formik.errors.address?.city)
                                    }
                                    helperText={
                                        formik.touched.address?.city &&
                                        formik.errors.address?.city
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label={t('doctors.address.state')}
                                    name="address.state"
                                    value={formik.values.address.state}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.address?.state &&
                                        Boolean(formik.errors.address?.state)
                                    }
                                    helperText={
                                        formik.touched.address?.state &&
                                        formik.errors.address?.state
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label={t('doctors.address.country')}
                                    name="address.country"
                                    value={formik.values.address.country}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    error={
                                        formik.touched.address?.country &&
                                        Boolean(formik.errors.address?.country)
                                    }
                                    helperText={
                                        formik.touched.address?.country &&
                                        formik.errors.address?.country
                                    }
                                />
                            </Grid>

                            {/* Active Toggle */}
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formik.values.active}
                                            onChange={(e) =>
                                                formik.setFieldValue(
                                                    'active',
                                                    e.target.checked
                                                )
                                            }
                                            name="active"
                                        />
                                    }
                                    label={t('doctors.active')}
                                />
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/* ════════════════════════════════════════════════════
                        TAB 1 — CLINICS
                    ════════════════════════════════════════════════════ */}
                    <TabPanel value={activeTab} index={1}>
                        {/* Clinic validation alert */}
                        {formSubmitted && clinicErrors.some(Boolean) && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {t(
                                    'doctors.clinics.nameRequiredAll',
                                    'All clinics must have a name.'
                                )}
                            </Alert>
                        )}

                        {/* Empty state */}
                        {clinics.length === 0 ? (
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                py={6}
                                gap={2}
                            >
                                <ClinicIcon
                                    sx={{ fontSize: 56, color: 'text.disabled' }}
                                />
                                <Typography color="text.secondary">
                                    {t(
                                        'doctors.clinics.noClinics',
                                        'No clinics added yet.'
                                    )}
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddClinic}
                                >
                                    {t(
                                        'doctors.clinics.addClinic',
                                        'Add Clinic'
                                    )}
                                </Button>
                            </Box>
                        ) : (
                            <>
                                {/* Clinic list */}
                                {clinics.map((clinic, index) => (
                                    <ClinicForm
                                        key={index}
                                        clinic={clinic}
                                        index={index}
                                        representatives={representatives}
                                        onUpdate={handleUpdateClinic}
                                        onRemove={handleRemoveClinic}
                                        t={t}
                                    />
                                ))}

                                {/* Add another clinic */}
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddClinic}
                                    fullWidth
                                    sx={{ mt: 1 }}
                                >
                                    {t(
                                        'doctors.clinics.addAnotherClinic',
                                        'Add Another Clinic'
                                    )}
                                </Button>
                            </>
                        )}
                    </TabPanel>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                {/* Quick-add clinic shortcut when on general tab */}
                {activeTab === 0 && (
                    <Button
                        variant="text"
                        startIcon={<AddIcon />}
                        onClick={handleAddClinic}
                        sx={{ mr: 'auto' }}
                    >
                        {t('doctors.clinics.addClinic', 'Add Clinic')}
                    </Button>
                )}
                <Button onClick={handleClose}>{t('common.cancel')}</Button>
                <Button
                    type="submit"
                    form="doctor-form"
                    variant="contained"
                    disabled={loading}
                    startIcon={loading && <CircularProgress size={20} />}
                >
                    {doctor ? t('common.update') : t('common.create')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddDoctorDialog;